'use client';

import React from 'react';
import { X } from 'lucide-react';
import { useCatalogContext } from '@/core/components/Builder/CatalogContext';
import { useTranslation } from '@/core/components/providers/TranslationProvider';
import { humanizeFacetKey } from './catalog-filter-utils';
import { useSiteConfig } from '@/core/components/providers/SiteConfigProvider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/core/components/ui/select';

// '' (no ?sort=) = the shop's own default order; Radix Select can't hold an empty value, hence DEFAULT_SORT.
const DEFAULT_SORT = 'default';
const SORT_OPTIONS: Array<[string, string, string]> = [
    [DEFAULT_SORT, 'filters.sort_default', 'Polecane'],
    ['popular', 'filters.sort_popular', 'Najpopularniejsze'],
    ['newest', 'filters.sort_newest', 'Najnowsze'],
    ['price_asc', 'filters.sort_price_asc', 'Cena: od najniższej'],
    ['price_desc', 'filters.sort_price_desc', 'Cena: od najwyższej'],
    ['rating', 'filters.sort_rating', 'Najlepiej oceniane'],
    ['discount', 'filters.sort_discount', 'Największa obniżka'],
    ['name', 'filters.sort_name', 'Nazwa A–Z'],
];

export function CatalogToolbar({ preview = false }: { preview?: boolean }) {
    const ctx = useCatalogContext();
    const { t, currency } = useTranslation();
    const siteConfig = useSiteConfig();
    // Filters bring the product count and active-filter chips; the sort select has its own switch
    // (Produkty → Filtry i sortowanie), so a shop can offer sorting without filters.
    const filtersOn = preview || (Array.isArray(siteConfig.catalog_filters) && siteConfig.catalog_filters.length > 0);
    const sortOn = preview || Boolean(siteConfig.catalog_sort_enabled);
    if (!filtersOn && !sortOn) return null;
    if (!ctx) {
        if (!preview) return null;
        return (
            <div className="mb-5 flex items-center justify-between gap-3 opacity-70">
                <span className="text-sm opacity-60">{t('category.products_count', '{n} produktów').replace('{n}', '12')}</span>
                <div className="flex items-center gap-2 text-sm">
                    <span className="opacity-60">{t('filters.sort_label', 'Sortuj')}:</span>
                    <Select value={DEFAULT_SORT} disabled>
                        <SelectTrigger className="h-9 w-auto min-w-[10rem] rounded-xl border-current/15 bg-transparent text-sm font-medium">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={DEFAULT_SORT}>{t('filters.sort_default', 'Polecane')}</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>
        );
    }

    const facetLabels = new Map<string, string>();
    for (const facet of ctx.facets?.facets || []) {
        for (const item of facet.values) facetLabels.set(`${facet.key}:${item.value}`, item.label);
    }

    const chips: Array<{ id: string; label: string; clear: () => void }> = [];
    if (ctx.filters.query.trim()) {
        chips.push({
            id: 'query',
            label: `${t('filters.search', 'Wyszukiwarka')}: ${ctx.filters.query.trim()}`,
            clear: () => ctx.setFilters((prev) => ({ ...prev, query: '' })),
        });
    }
    if (ctx.filters.minPrice || ctx.filters.maxPrice) {
        chips.push({
            id: 'price',
            label: `${ctx.filters.minPrice || '0'}–${ctx.filters.maxPrice || '∞'} ${currency}`,
            clear: () => ctx.setFilters((prev) => ({ ...prev, minPrice: '', maxPrice: '' })),
        });
    }
    if (ctx.filters.subcategoryId !== null) {
        const subcategory = ctx.subcategories.find((item) => item.id === ctx.filters.subcategoryId);
        chips.push({
            id: 'subcategory',
            label: subcategory?.name || t('filters.subcategories', 'Podkategorie'),
            clear: () => ctx.setFilters((prev) => ({ ...prev, subcategoryId: null })),
        });
    }
    for (const [key, values] of Object.entries(ctx.filters.facets)) {
        for (const value of values) {
            const fallback = humanizeFacetKey(value);
            const rawLabel = facetLabels.get(`${key}:${value}`) || fallback;
            const label = key === '@condition' ? t(`product.condition.${value}`, rawLabel) : rawLabel;
            chips.push({
                id: `${key}:${value}`,
                label,
                clear: () => ctx.setFilters((prev) => {
                    const facets = { ...prev.facets };
                    const remaining = (facets[key] || []).filter((item) => item !== value);
                    if (remaining.length) facets[key] = remaining;
                    else delete facets[key];
                    return { ...prev, facets };
                }),
            });
        }
    }

    return (
        <div className={`mb-5 flex flex-col gap-3 sm:flex-row sm:items-center ${filtersOn ? 'sm:justify-between' : 'sm:justify-end'}`}>
            {filtersOn && <div className="flex w-full min-w-0 flex-1 flex-wrap items-center gap-2">
                <span className="text-sm text-current opacity-60">
                    {t('category.products_count', '{n} produktów').replace('{n}', String(ctx.productCount))}
                </span>
                {chips.map((chip) => (
                    <button
                        key={chip.id}
                        type="button"
                        onClick={chip.clear}
                        className="inline-flex max-w-full items-center gap-1 rounded-full border border-current/15 bg-current/[0.04] px-3 py-1.5 text-xs font-semibold transition-colors hover:border-primary/40 hover:text-primary"
                        aria-label={`${t('filters.remove', 'Usuń filtr')}: ${chip.label}`}
                    >
                        <span className="truncate">{chip.label}</span>
                        <X className="h-3 w-3 shrink-0" />
                    </button>
                ))}
                {chips.length > 1 && (
                    <button
                        type="button"
                        onClick={() => ctx.setFilters({ query: '', minPrice: '', maxPrice: '', subcategoryId: null, facets: {} })}
                        className="text-xs font-semibold text-current opacity-50 hover:opacity-100"
                    >
                        {t('filters.clear_all', 'Wyczyść wszystko')}
                    </button>
                )}
            </div>}
            {sortOn && <div className="flex w-full shrink-0 items-center gap-2 text-sm sm:w-auto">
                <span className="shrink-0 opacity-60">{t('filters.sort_label', 'Sortuj')}:</span>
                <Select
                    value={ctx.sort || DEFAULT_SORT}
                    onValueChange={(value) => ctx.setSort(value === DEFAULT_SORT ? '' : value)}
                >
                    <SelectTrigger
                        aria-label={t('filters.sort_label', 'Sortuj')}
                        className="h-9 min-w-0 flex-1 rounded-xl border-current/15 bg-transparent text-sm font-medium sm:w-auto sm:min-w-[12rem] sm:flex-none"
                    >
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {SORT_OPTIONS.map(([value, key, fallback]) => (
                            <SelectItem key={value} value={value}>{t(key, fallback)}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>}
        </div>
    );
}
